
  import { Vue, Component, Prop } from "vue-property-decorator";
  import InfiniteLoading, { StateChanger } from "vue-infinite-loading";

  import GET_NOTIFICATIONS from "@/apollo/notifications/GetNotifications.gql";

  import UIButton from "@/components/button/ui-button.vue";
  import NotificationItem from "@/components/notification/NotificationItem.vue";
  import PageHeadContainer from "@/components/page/PageHeadContainer.vue";
  import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
  import MarkAllAsRead from "@/components/notification/MarkAllAsRead.vue";
  import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
  import DeleteReadNotifications from "@/components/notification/DeleteReadNotifications.vue";

  import { Notification, User } from "@/generated/graphql";
  import { NOTIFICATION, SHOW_ALL_NOTIFICATIONS } from "@/entities/Events";
  import Spinner from "@/components/common/Spinner.vue";
  import Message from "@/data/notificationsMessages";
  import EmptyListImageInbox from "@/components/icons/emptyListImages/EmptyListImageInbox.vue";

  interface NotificationsData {
    notifications: Notification[];
    count: number;
    alreadyReadCount: number;
  }

  @Component({
    components: {
      EmptyListImageInbox,
      UIButton,
      NotificationItem,
      PageHeadContainer,
      LoadingOverlay,
      MarkAllAsRead,
      EmptyListPlaceholder,
      DeleteReadNotifications,
      InfiniteLoading,
      Spinner,
    },
  })
  export default class AllNotifications extends Vue {
    @Prop({ type: Object, required: true }) readonly currentUser!: User;

    limit = 10;
    offset = 0;
    infiniteId = 0;
    processingMarkAsRead: boolean = false;
    processingDeleteRead: boolean = false;
    dataLoaded: boolean = false;
    infiniteScrollShown: boolean = true;

    notificationsData: NotificationsData = {
      notifications: [],
      count: 0,
      alreadyReadCount: 0,
    };

    get processing(): boolean {
      return this.processingMarkAsRead || this.processingDeleteRead;
    }

    get isNotificationsExist(): boolean {
      return this.notificationsData.notifications.length > 0;
    }

    get isMarkAllAsReadDisabled(): boolean {
      return this.notificationsData.notifications.every((notification) => notification.read === true) || this.processing;
    }

    get isNotificationsListEmpty(): boolean {
      return !this.isNotificationsExist && this.dataLoaded;
    }

    get isDeleteReadDisabled(): boolean {
      return this.notificationsData.alreadyReadCount === 0 || this.processing;
    }

    created(): void {
      this.$eventBus.listen(SHOW_ALL_NOTIFICATIONS, this.updateNotificationsList);
    }

    beforeDestroy(): void {
      this.$eventBus.remove(SHOW_ALL_NOTIFICATIONS, this.updateNotificationsList);
    }

    async loadMore($state: StateChanger): Promise<void> {
      if (this.offset > this.notificationsData.count) {
        $state.complete();
        return;
      }

      const data = await this.getNotifications();
      this.dataLoaded = true;
      this.notificationsData = {
        notifications: [...this.notificationsData.notifications, ...data.notifications],
        count: data.count,
        alreadyReadCount: data.alreadyReadCount,
      };
      this.offset += this.limit;
      $state.loaded();
    }

    markNotificationAsRead(id: string): void {
      const notifications = this.notificationsData.notifications.map((notification) => {
        if (notification.id === id) {
          notification.read = true;
        }
        return notification;
      });

      const alreadyReadCount = this.notificationsData.alreadyReadCount + 1;

      this.notificationsData = {
        ...this.notificationsData,
        notifications,
        alreadyReadCount,
      };
    }

    markAllAsRead(): void {
      const notifications = this.notificationsData.notifications.map((notification) => {
        notification.read = true;
        return notification;
      });

      this.notificationsData = {
        ...this.notificationsData,
        notifications,
        alreadyReadCount: this.notificationsData.count,
      };
    }

    removeNotification(id: string): void {
      const notifications = this.notificationsData.notifications.filter((notification) => {
        if (notification.id === id) {
          return;
        }
        return notification;
      });

      const alreadyReadCount = this.notificationsData.alreadyReadCount - 1;
      const count = this.notificationsData.count - 1;

      this.notificationsData = {
        notifications,
        count,
        alreadyReadCount,
      };
    }

    removeAllNotifications(): void {
      const notifications = this.notificationsData.notifications.filter((notification) => {
        if (notification.read) {
          return;
        }
        return notification;
      });

      this.notificationsData = {
        notifications,
        count: notifications.length + 1,
        alreadyReadCount: 0,
      };
    }

    private updateNotificationsList() {
      this.offset = 0;
      this.notificationsData = {
        notifications: [],
        count: 0,
        alreadyReadCount: 0,
      };
      this.infiniteId += 1;
    }

    private async getNotifications(offset = this.offset): Promise<NotificationsData> {
      try {
        const notificationsDataRaw = await this.$apollo.query({
          query: GET_NOTIFICATIONS,
          variables: {
            userId: this.currentUser.id,
            limit: this.limit,
            offset: offset,
          },
          fetchPolicy: "no-cache",
        });
        const data = notificationsDataRaw.data;
        return {
          notifications: data.notifications,
          count: data.total.aggregate.count,
          alreadyReadCount: data.alreadyReadCount.aggregate.count,
        };
      } catch (e) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
        return {
          notifications: [],
          count: 0,
          alreadyReadCount: 0,
        };
      }
    }
  }
